/**
 * 支給デザインのマイページトップのメニュータイル（`rectangle-item`）。
 *
 * `rectangle-*` は**支給101本のうち `my-page/index.html` 1本にしか出ない**ので部品化しない。
 * 支給の `href` はデザイン側のファイル名なので、飛び先は既存のルート棚卸しから決める。
 * 飛び先5件は `MYPAGE_SIDEBAR_NAV_ITEMS` の先頭5件と同じだが、**サイドバーは6件・タイルは5件で
 * 意味が違う**ので、片方から導出せずそれぞれ持つ。
 *
 * **ラベルは支給に従う**（#1253 で決めた基準）。旧 `FUNCTIONAL_CATEGORIES` とは
 * `お客様情報変更` → `お客様情報`、`定期お届け情報` → `定期コース情報`、
 * `アンケートキャンペーン` → `キャンペーン情報` の3件がずれていた。
 *
 * **ただしアンケートキャンペーンだけは支給に従わない**（決定#78・2026-08-19 事業側判断）。
 * 支給は25箇所とも「キャンペーン情報」だが、飛び先の `/customer-enquete` が扱うのは
 * **アンケートに答えると特典が届くキャンペーンだけ**で、「キャンペーン情報」では
 * 割引や送料無料の告知も含むように読める。変換前の呼び名に戻す。
 */
export const MYPAGE_MENU_ITEMS: ReadonlyArray<{ label: string; to: string; icon: string }> = [
  { label: 'お客様情報', to: '/mypage/mypage-base', icon: '/image/my-page/mypage-icon_info.svg' },
  {
    label: '購入履歴',
    to: '/mypage/order-history',
    icon: '/image/my-page/mypage-icon_history.png',
  },
  {
    label: '定期コース情報',
    to: '/mypage/regular-info',
    icon: '/image/my-page/mypage-icon_regular.png',
  },
  { label: 'お問い合わせ', to: '/mypage/contact', icon: '/image/my-page/mypage-icon_contact.png' },
  {
    label: 'アンケートキャンペーン',
    to: '/customer-enquete',
    icon: '/image/my-page/mypage-icon_campaign.png',
  },
] as const;

/**
 * 支給デザインのマイページ用サイドバーナビ（`my-page-sidebar-nav`）。
 *
 * **支給のマイページ7本すべてで同一の6件**（`design-src/20260810/my-page/contact.html:326-369` ほか）。
 * 支給の `href` はデザイン側のファイル名なので、飛び先は既存のルート棚卸しから決める（決定#33）。
 * 6件は旧 `SideMypageConst.LINK_LIST`（5件）と `FUNCTIONAL_CATEGORIES`（5件）の和集合に一致していた
 * （どちらも削除済み）。
 *
 * - `アンケートキャンペーン` … 支給の `campaign-info.html` は `/customer-enquete` の再スキンなので
 *   そこへ飛ばす（`docs/design-renewal/routes.tsv`）。**ラベルは支給の「キャンペーン情報」を使わない**（決定#78）
 * - `定期コース情報` … 支給の `href` は `./details.html` だが、そのファイルは支給物に存在しない
 *
 * **ラベルは支給に従う**（`お客様情報の変更` → `お客様情報` など。#1253 で決めた基準）。
 *
 * 旧 `commons/side-mypage.vue` と `SideMypageConst.LINK_LIST` は #1335 で削除済み
 * （全ページの変換が終わり参照が0になった）。
 */
export const MYPAGE_SIDEBAR_NAV_ITEMS: ReadonlyArray<{ label: string; to: string }> = [
  { label: 'お客様情報', to: '/mypage/mypage-base' },
  { label: '購入履歴', to: '/mypage/order-history' },
  { label: '定期コース情報', to: '/mypage/regular-info' },
  { label: 'お問い合わせ', to: '/mypage/contact' },
  { label: 'アンケートキャンペーン', to: '/customer-enquete' },
  { label: '退会のお申し込み', to: '/mypage/withdrawal' },
] as const;

/**
 * マイページのキャンペーンで［通常価格］と「◯円お得!」を出さないセットコード。
 *
 * プレゼント同梱セットは通常価格にプレゼント分が乗るため、値引き額が過大に見える。
 * raffinee `app/Consts/SetsConst.php` の `$price_hidden_set_codes` の移植（`決定#103`・issue #1412）。
 *
 * **raffinee は7件だがここは4件。** `S54` `S55` `G08` は販売終了のため落とした。
 * **キャンペーン対象セットは毎月入れ替わるが、この定数は自動追随しない。**
 * 本来は `sets` テーブルで持ち管理画面から切り替えるべきで、この定数は暫定（issue #1412）。
 */
export const CAMPAIGN_PRICE_HIDDEN_SET_CODES: ReadonlyArray<string> = [
  'S51',
  'S52',
  'S53',
  'S56',
] as const;

/**
 * マイページのキャンペーンで価格の前に「1本あたり」を出すセットコード。
 * `price` が1セットの総額ではなく1本あたりの単価なので、付けないと3本分の値段に見える。
 *
 * raffinee `SetsConst::$special_set_codes` の移植（`決定#103`）。
 * 単位は raffinee のベタ書き文言に合わせて「本」で固定する。
 * **商品別 const の `unitLabel`（`consts/products/520.ts` は `箱`）とは別系統なので引かない。**
 */
export const CAMPAIGN_UNIT_PRICE_SET_CODES: ReadonlyArray<string> = ['F25'] as const;
